import { Box, Button, Typography } from '@mui/material';
import { FloppyDiskIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { FC, type PropsWithChildren, type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InputMaybe } from 'src/graphql/generated/schema';

interface FieldEditProps {
  title: string;
  value?: InputMaybe<string> | number | ReactNode | undefined;
  onSave: () => void;
  onCancel: () => void;
}

const FieldEditComponent: FC<PropsWithChildren<FieldEditProps>> = ({ onSave, onCancel, value, title, children }) => {
  const { t } = useTranslation('translation');

  const [editMode, setEditMode] = useState(false);

  const save = useCallback(() => {
    try {
      onSave();
      setEditMode(false);
    } catch (e) {
      setEditMode(true);
    }
  }, [onSave, setEditMode]);

  const cancel = useCallback(() => {
    onCancel();
    setEditMode(false);
  }, [onCancel, setEditMode]);

  const valueContainsNewLine = value?.toString().includes('\n');

  return (
    <Box sx={{ borderBottom: '1px solid #DDDDDD', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6-bold">{title}</Typography>
        {editMode ? (
          <Button sx={{ marginLeft: 'auto' }} variant="text" onClick={() => cancel()}>
            {t('pages.my-profile-page.hide-btn')}
          </Button>
        ) : (
          <Button startIcon={<PencilSimpleIcon />} variant={'text'} onClick={() => setEditMode(true)}>
            {t('pages.my-profile-page.change-btn')}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 2,
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        {editMode ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexBasis: '100%', flexWrap: 'wrap' }}>
            {children}

            <Button
              size="small"
              startIcon={<FloppyDiskIcon />}
              sx={{ marginRight: 'auto' }}
              variant="contained"
              onClick={() => save()}
            >
              {t('pages.my-profile-page.submit-btn')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {valueContainsNewLine
              ? value
                  ?.toString()
                  .split('\n')
                  .map((paragraph, i) => <Typography key={i}>{paragraph}</Typography>)
              : value}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FieldEditComponent;
